import React, { useEffect, useRef } from "react";
import { EXAM_CONFIG, EXAM_MESSAGES } from "../utils/AssessmentStrings";
import { ScreenRecorderProps } from "../interface/camera";

import html2canvas from "html2canvas";
import { saveMedia } from "../utils/AssessmentStorage";

/**
 * Interval at which a silent screenshot of the screen is captured and stored.
 */
const CAPTURE_INTERVAL_MS = EXAM_CONFIG.SCREEN_CAPTURE_INTERVAL_MS;

let globalScreenStreamPromise: Promise<MediaStream> | null = null;
let activeRecorders = 0;

/**
 * ScreenRecorder Component
 *
 * A background component designed to capture the user's screen at a fixed interval.
 * It enforces proper exam conditions by verifying that the user selects the "Entire Screen" option.
 */
const ScreenRecorder: React.FC<ScreenRecorderProps> = ({
  onPermissionDenied,
  onStreamStop,
  onReady,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const intervalRef = useRef<number | null>(null);

  // Refs are necessary to provide static access to dynamically changing props
  // without severing the lifecycle of our singleton video stream.
  const onReadyRef = useRef(onReady);
  const onStreamStopRef = useRef(onStreamStop);
  useEffect(() => {
    onReadyRef.current = onReady;
    onStreamStopRef.current = onStreamStop;
  }, [onReady, onStreamStop]);

  useEffect(() => {
    let active = true;
    activeRecorders++;

    const startScreenShare = async () => {
      try {
        // --- MOBILE/UNSUPPORTED FALLBACK ---
        // 'getDisplayMedia' is not supported on mobile browsers (iOS Safari, Android Chrome).
        // If the user is on mobile, we gracefully bypass screen recording so they can take the exam.
        const isMobile =
          /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent,
          );
        if (
          isMobile ||
          !navigator.mediaDevices ||
          !navigator.mediaDevices.getDisplayMedia
        ) {
          console.warn(
            "Screen sharing is not supported on this device. Bypassing screen record step.",
          );

          // --- MOBILE SCREEN CAPTURE FALLBACK ---
          // Use html2canvas to capture the page content periodically on mobile.
          const capturePage = async () => {
            if (!active) return;
            try {
              const canvas = await html2canvas(document.body, {
                scale: 1.5, // Better clarity for mobile screens
                logging: false,
                useCORS: true,
              });
              const dataUrl = canvas.toDataURL("image/webp", 0.85);
              saveMedia("examScreenCaptures", {
                timestamp: new Date().toISOString(),
                image: dataUrl,
              });
              console.log("Mobile page capture saved");
            } catch (e) {
              console.error("Mobile page capture failed:", e);
            }
          };

          if (active && onReadyRef.current) {
            onReadyRef.current();
            // Start periodic page capture
            capturePage();
            intervalRef.current = window.setInterval(
              capturePage,
              CAPTURE_INTERVAL_MS,
            );
          }
          return;
        }
        // -----------------------------------

        if (globalScreenStreamPromise) {
          try {
            const existingStream = await globalScreenStreamPromise;
            const track = existingStream.getVideoTracks()[0];
            if (!track || track.readyState === "ended") {
              globalScreenStreamPromise = null;
            }
          } catch {
            globalScreenStreamPromise = null;
          }
        }

        if (!globalScreenStreamPromise) {
          globalScreenStreamPromise = navigator.mediaDevices.getDisplayMedia({
            video: { displaySurface: "monitor" } as any,
            audio: false,
          });
        }

        const stream = await globalScreenStreamPromise;

        if (!active) {
          return;
        }

        const videoTrack = stream.getVideoTracks()[0];

        // Strict check to ensure the user shared the entire screen
        const settings = videoTrack.getSettings();
        const displaySurface = (settings as any).displaySurface;
        if (displaySurface && displaySurface !== "monitor") {
          // If we fail the strict check, we stop tracks and reset the global promise
          stream.getTracks().forEach((t) => t.stop());
          globalScreenStreamPromise = null;
          alert(EXAM_MESSAGES.SCREEN_SHARE_REQUIREMENT);
          if (active) onPermissionDenied();
          return;
        }

        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;

        video.srcObject = stream;

        if (active && onReadyRef.current) {
          onReadyRef.current();
        }

        if (videoTrack) {
          const handleEnded = () => {
            globalScreenStreamPromise = null;
            if (active && onStreamStopRef.current) onStreamStopRef.current();
          };
          videoTrack.addEventListener("ended", handleEnded);
        }

        let captureStarted = false;
        const startCaptureTimer = () => {
          if (!active || captureStarted) return;
          captureStarted = true;
          captureImage();
          if (intervalRef.current) clearInterval(intervalRef.current);
          intervalRef.current = window.setInterval(
            captureImage,
            CAPTURE_INTERVAL_MS,
          );
        };

        video
          .play()
          .then(() => {
            if (!active) return;
            setTimeout(startCaptureTimer, 1500);
          })
          .catch((err) => {
            console.warn("Screen video play failed:", err);
            setTimeout(startCaptureTimer, 1500);
          });

        video.addEventListener(
          "playing",
          () => {
            if (!active) return;
            setTimeout(startCaptureTimer, 1500);
          },
          { once: true },
        );
      } catch (err) {
        console.error("Screen sharing error:", err);
        globalScreenStreamPromise = null;
        if (active) onPermissionDenied();
      }
    };

    /**
     * Parses the stringified base64 image and saves it securely along with a timestamp to IndexedDB.
     */
    const saveImage = (base64Image: string) => {
      saveMedia("examScreenCaptures", {
        timestamp: new Date().toISOString(),
        image: base64Image,
      });
      console.log("Screen captured and saved to IndexedDB");
    };

    /**
     * Captures the current frame of the hidden video element and pushes it to local storage.
     */
    const captureImage = () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas) return;

      if (
        video.readyState < 2 ||
        video.videoWidth === 0 ||
        video.videoHeight === 0
      ) {
        return;
      }

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL("image/webp", 0.85);

      if (dataUrl.length < 5000) {
        return;
      }

      saveImage(dataUrl);
    };

    startScreenShare();

    return () => {
      active = false;
      activeRecorders--;
      if (intervalRef.current) clearInterval(intervalRef.current);

      // Delay stopping the stream to allow React 18 Strict Mode remount
      setTimeout(() => {
        if (activeRecorders === 0 && globalScreenStreamPromise) {
          globalScreenStreamPromise
            .then((s) => s.getTracks().forEach((t) => t.stop()))
            .catch(() => {});
          globalScreenStreamPromise = null;
        }
      }, 500);
    };
  }, [onPermissionDenied]);

  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        bottom: 0,
        right: 0,
        width: "1px",
        height: "1px",
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: -1,
      }}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{ width: "160px", height: "120px", display: "block" }}
      />
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
};

export default ScreenRecorder;
